/**
 * Restaurantele din cont: listă, creare, redenumire, ștergere.
 *
 * Fiecare acțiune este confirmată înainte de a fi trimisă, iar erorile de plan (403 „ai atins
 * limita") sunt afișate ca mesaj de business, cu trimitere la pagina de abonament.
 */
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Clock24,
  ConfirmDialog,
  EmptyState,
  Field,
  Icon,
  InlineAlert,
  Input,
  Modal,
  PageHeader,
  Select,
  Skeleton,
  Switch,
} from '../components/ui.js';
import { api } from '../lib/api.js';
import { formatDate, useResource } from '../lib/hooks.js';
import { errorMessage, fieldErrors, useSession } from '../lib/session.js';
import { useToast } from '../components/ui.js';

interface Restaurant {
  id: string;
  public_id: string;
  slug: string;
  name: string;
  currency: string;
  timezone: string;
  default_locale: string;
  created_at: string;
  locations_count?: number;
}

const CURRENCIES = ['RON', 'EUR', 'USD', 'GBP'];
const TIMEZONES = ['Europe/Bucharest', 'Europe/Chisinau', 'Europe/Vienna', 'Europe/Rome', 'Europe/Berlin', 'Europe/Madrid'];
const WEEKDAYS = ['Luni', 'Marți', 'Miercuri', 'Joi', 'Vineri', 'Sâmbătă', 'Duminică'];

interface RestaurantHour {
  weekday: number;
  opens_at: string | null;
  closes_at: string | null;
  is_closed: boolean;
}

interface RestaurantSchedule {
  alwaysOpen: boolean;
  hours: RestaurantHour[];
}

export function RestaurantsPage() {
  const session = useSession();
  const toast = useToast();
  const resource = useResource<Restaurant[]>(() => api.get<Restaurant[]>('/restaurants'), [session.activeAccountId]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Restaurant | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Restaurant | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: '', currency: 'RON', timezone: 'Europe/Bucharest' });
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [schedule, setSchedule] = useState<RestaurantSchedule | null>(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);

  const canCreate = session.can('restaurant.create');
  const canUpdate = session.can('restaurant.update');
  const canDelete = session.can('restaurant.delete');

  async function create() {
    setBusy(true);
    setFormError(null);
    setErrors({});
    try {
      await api.post('/restaurants', form);
      toast.success('Restaurant creat', 'Îl poți configura imediat: locații, meniu, QR-uri.');
      setCreateOpen(false);
      setForm({ name: '', currency: 'RON', timezone: 'Europe/Bucharest' });
      await resource.reload();
    } catch (error) {
      setErrors(fieldErrors(error));
      setFormError(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function openEdit(restaurant: Restaurant) {
    setFormError(null);
    setEditTarget(restaurant);
    setSchedule(null);
    setScheduleLoading(true);
    try {
      setSchedule(await api.get<RestaurantSchedule>(`/restaurants/${restaurant.id}/hours`));
    } catch (error) {
      setFormError(errorMessage(error));
    } finally {
      setScheduleLoading(false);
    }
  }

  async function saveEdit() {
    if (!editTarget || !schedule) return;
    setBusy(true);
    setFormError(null);
    try {
      await api.patch(`/restaurants/${editTarget.id}`, { name: editTarget.name, currency: editTarget.currency });
      await api.put(`/restaurants/${editTarget.id}/hours`, {
        alwaysOpen: schedule.alwaysOpen,
        hours: schedule.hours.map((row) => ({
          weekday: row.weekday,
          isClosed: row.is_closed,
          opensAt: row.opens_at?.slice(0, 5) ?? null,
          closesAt: row.closes_at?.slice(0, 5) ?? null,
        })),
      });
      toast.success('Modificări salvate', schedule.alwaysOpen ? 'Restaurantul este deschis non-stop.' : 'Programul se aplică la meniuri și la codurile QR.');
      setEditTarget(null);
      setSchedule(null);
      await resource.reload();
    } catch (error) {
      setFormError(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await api.del(`/restaurants/${deleteTarget.id}`);
      toast.success('Restaurant șters', 'Datele asociate au fost arhivate.');
      setDeleteTarget(null);
      await resource.reload();
    } catch (error) {
      toast.error('Nu am putut șterge restaurantul', errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page-enter">
      <PageHeader
        title="Restaurante"
        subtitle="Fiecare restaurant are meniul lui, publicat către toate canalele."
        actions={
          canCreate ? (
            <Button icon={<Icon name="plus" size={17} />} onClick={() => setCreateOpen(true)}>
              Restaurant nou
            </Button>
          ) : null
        }
      />

      {resource.loading ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {Array.from({ length: 2 }).map((_, index) => (
            <Card key={index} className="flex flex-col gap-3">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-9 w-full" />
            </Card>
          ))}
        </div>
      ) : resource.error ? (
        <InlineAlert tone="warning" title="Nu am putut încărca restaurantele">
          {resource.error}{' '}
          <button type="button" className="font-medium underline" onClick={() => void resource.reload()}>
            Reîncearcă
          </button>
        </InlineAlert>
      ) : !resource.data?.length ? (
        <EmptyState
          icon={<Icon name="store" size={28} />}
          title="Niciun restaurant încă"
          description="Creează primul restaurant, apoi adaugă locațiile și meniul."
          action={
            canCreate ? (
              <Button icon={<Icon name="plus" size={17} />} onClick={() => setCreateOpen(true)}>
                Creează restaurant
              </Button>
            ) : null
          }
        />
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2">
          {resource.data.map((restaurant) => (
            <Card as="li" key={restaurant.id} className="flex flex-col gap-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 flex-col gap-1">
                  <h3 className="truncate font-display text-title text-ink-950">{restaurant.name}</h3>
                  <p className="font-mono text-caption text-ink-400">/{restaurant.slug}</p>
                </div>
                <Badge tone="neutral">{restaurant.currency}</Badge>
              </div>

              <dl className="grid grid-cols-2 gap-3 text-body-s">
                <div className="flex flex-col">
                  <dt className="text-caption text-ink-400">Fus orar</dt>
                  <dd className="text-ink-950">{restaurant.timezone}</dd>
                </div>
                <div className="flex flex-col">
                  <dt className="text-caption text-ink-400">Limba de bază</dt>
                  <dd className="text-ink-950">{restaurant.default_locale.toUpperCase()}</dd>
                </div>
                <div className="flex flex-col">
                  <dt className="text-caption text-ink-400">Creat</dt>
                  <dd className="text-ink-950">{formatDate(restaurant.created_at)}</dd>
                </div>
              </dl>

              <div className="mt-auto flex flex-wrap gap-2">
                {canUpdate ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => void openEdit(restaurant)}
                  >
                    Editează
                  </Button>
                ) : null}
                {canDelete ? (
                  <Button variant="danger" size="sm" onClick={() => setDeleteTarget(restaurant)}>
                    Șterge
                  </Button>
                ) : null}
              </div>
            </Card>
          ))}
        </ul>
      )}

      {/* Creare */}
      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Restaurant nou"
        description="Poți adăuga locațiile imediat după creare."
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreateOpen(false)} disabled={busy}>
              Anulează
            </Button>
            <Button loading={busy} onClick={() => void create()}>
              Creează
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {formError ? <InlineAlert tone="danger">{formError}</InlineAlert> : null}
          <Field label="Numele restaurantului" required errors={errors.name}>
            {(props) => (
              <Input
                {...props}
                value={form.name}
                onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))}
                placeholder="Pizzeria Roma"
              />
            )}
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Monedă">
              {(props) => (
                <Select
                  {...props}
                  value={form.currency}
                  onChange={(e) => setForm((current) => ({ ...current, currency: e.target.value }))}
                >
                  {CURRENCIES.map((currency) => (
                    <option key={currency} value={currency}>
                      {currency}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Fus orar">
              {(props) => (
                <Select
                  {...props}
                  value={form.timezone}
                  onChange={(e) => setForm((current) => ({ ...current, timezone: e.target.value }))}
                >
                  {TIMEZONES.map((zone) => (
                    <option key={zone} value={zone}>
                      {zone}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
        </div>
      </Modal>

      {/* Editare */}
      <Modal
        open={Boolean(editTarget)}
        onClose={() => setEditTarget(null)}
        title="Editează restaurantul"
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditTarget(null)} disabled={busy}>
              Anulează
            </Button>
            <Button loading={busy} disabled={!schedule} onClick={() => void saveEdit()}>
              Salvează
            </Button>
          </>
        }
      >
        {editTarget ? (
          <div className="flex flex-col gap-4">
            {formError ? <InlineAlert tone="danger">{formError}</InlineAlert> : null}
            <Field label="Numele restaurantului" required>
              {(props) => (
                <Input
                  {...props}
                  value={editTarget.name}
                  onChange={(e) => setEditTarget({ ...editTarget, name: e.target.value })}
                />
              )}
            </Field>
            <Field label="Monedă">
              {(props) => (
                <Select
                  {...props}
                  value={editTarget.currency}
                  onChange={(e) => setEditTarget({ ...editTarget, currency: e.target.value })}
                >
                  {CURRENCIES.map((currency) => (
                    <option key={currency} value={currency}>
                      {currency}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <p className="text-caption text-ink-400">
              Slug-ul public rămâne neschimbat ca să nu se rupă linkurile deja distribuite.
            </p>
            <div className="border-t border-ink-100 pt-4">
              <h3 className="text-body-s font-medium text-ink-950">Program de funcționare</h3>
              <p className="mt-1 text-caption text-ink-400">
                Orele sunt ora României (București). Meniurile și codurile QR rămân vizibile în afara programului și afișează mesajul că restaurantul este închis.
              </p>
              {scheduleLoading || !schedule ? (
                <div className="mt-3 flex flex-col gap-2">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <Skeleton key={index} className="h-10" />
                  ))}
                </div>
              ) : (
                <div className="mt-3 flex flex-col gap-3">
                  <Switch
                    checked={schedule.alwaysOpen}
                    onChange={(value) => setSchedule({ ...schedule, alwaysOpen: value })}
                    label="Non-stop"
                    description="Deschis în fiecare zi, fără oră de închidere."
                  />
                  {schedule.alwaysOpen ? null : (
                    <ul className="flex flex-col divide-y divide-ink-100">
                      {schedule.hours.map((row) => (
                        <li key={row.weekday} className="flex flex-wrap items-center gap-3 py-2.5 sm:flex-nowrap">
                          <span className="w-24 text-body-s text-ink-950">{WEEKDAYS[row.weekday]}</span>
                          <span className={row.is_closed ? 'flex items-center gap-2 opacity-40' : 'flex items-center gap-2'}>
                            <Clock24
                              value={row.opens_at?.slice(0, 5) ?? '10:00'}
                              disabled={row.is_closed}
                              onChange={(value) =>
                                setSchedule({
                                  ...schedule,
                                  hours: schedule.hours.map((item) =>
                                    item.weekday === row.weekday ? { ...item, opens_at: value } : item,
                                  ),
                                })
                              }
                            />
                            <span className="text-ink-400">–</span>
                            <Clock24
                              value={row.closes_at?.slice(0, 5) ?? '22:00'}
                              disabled={row.is_closed}
                              onChange={(value) =>
                                setSchedule({
                                  ...schedule,
                                  hours: schedule.hours.map((item) =>
                                    item.weekday === row.weekday ? { ...item, closes_at: value } : item,
                                  ),
                                })
                              }
                            />
                          </span>
                          <label className="ml-auto flex items-center gap-2 text-caption text-ink-600">
                            <input
                              type="checkbox"
                              checked={row.is_closed}
                              onChange={(event) =>
                                setSchedule({
                                  ...schedule,
                                  hours: schedule.hours.map((item) =>
                                    item.weekday === row.weekday ? { ...item, is_closed: event.target.checked } : item,
                                  ),
                                })
                              }
                              className="h-4 w-4 rounded border-ink-200"
                            />
                            Închis
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => void remove()}
        loading={busy}
        title={`Ștergi „${deleteTarget?.name ?? ''}"?`}
        description="Restaurantul, locațiile și meniurile lui vor fi arhivate. QR-urile nu vor mai funcționa."
        confirmLabel="Șterge restaurantul"
      />
    </div>
  );
}
